/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { lazy, Suspense } from 'react'
import type { ReactNode } from 'react'
import { Global } from '@emotion/react'
import { message } from 'antd'
import type { SelectProps } from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import type {
  ImportPlayerInput,
  ImportSeason,
} from '@sportninja/common/services/teamBalance'
import HeaderActionButton from 'src/components/ActionButton/HeaderActionButton'
import BaseModal from 'src/components/ts-components/BaseModal'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import {
  paragraphMedium,
  paragraphSmall,
  paragraphXSmall,
  weightMedium,
} from 'src/components/css'
import { useImportSelection } from '../hooks/useImportSelection'
import { guardMessage } from '../utils/balanceError'
import { balanceLabel, balanceText } from '../utils/balanceI18n'
import { importHints, importSeasonMeta } from '../utils/importDialogCopy'
import { ImportSelectionContext } from './cells/ImportSelectionContext'
import ImportPreviousSeasonsSkeleton from './ImportPreviousSeasonsSkeleton'
import InviteApprovalLock from './InviteApprovalLock'

/**
 * The grid holds AG Grid, so it loads only when the dialog opens; its skeleton
 * covers the wait, as it does while a season's players load.
 */
const ImportPlayerGrid = lazy(() => import('./ImportPlayerGrid'))

/** The dropdown's own class: the select's global style block stays untouched. */
const SEASON_POPUP_CLASS = 'import-season-popup'

type ImportPreviousSeasonsModalProps = {
  open: boolean
  teamId: string
  rosterId: string
  registrationId: string
  /** Newest first; the dialog opens on the newest. */
  seasons: readonly ImportSeason[]
  /**
   * Why nothing can be imported right now (approval, the lock): the dialog then
   * shows only that, and Send stays off.
   */
  blockedReason: string | null
  /** The sheet's import mutation is in flight. */
  submitting: boolean
  onSubmit: (body: {
    season_id: string
    players: ImportPlayerInput[]
  }) => Promise<unknown>
  onClose: () => void
}

const isConflict = (error: unknown): boolean =>
  (error as { status?: number } | null)?.status === 409

/** One season in the picker: "{season} · {team}", then its dates and size. */
type SeasonOption = {
  value: string
  label: string
  meta: string
  isSelected: boolean
}

/** The closed picker reads at body size, like the label above it. */
const renderClosedSeason = ({ label }: { label: ReactNode }) => (
  <span css={closedValueStyle}>{label}</span>
)

const isSeasonOption = (data: unknown): data is SeasonOption =>
  typeof data === 'object' &&
  data !== null &&
  'meta' in data &&
  'isSelected' in data

/** Two lines an option, and a check on the season shown. */
const renderSeasonOption: NonNullable<SelectProps['optionRender']> = (
  option
) => {
  const season = option.data
  if (!isSeasonOption(season)) {
    return option.label
  }

  return (
    <div css={optionStyle}>
      <span css={optionTextStyle}>
        <span css={optionTitleStyle}>{season.label}</span>
        <span css={optionMetaStyle}>{season.meta}</span>
      </span>
      {season.isSelected ? <CheckOutlined css={optionCheckStyle} /> : null}
    </div>
  )
}

/**
 * Jeff, Oct 1 — Import Players From Previous Seasons. Pick a previous season;
 * each player ticked gets a reserved slot and an email to register (the
 * invited-slot pipeline). Mounted only while open, so every open starts fresh on
 * the newest season.
 *
 * - Send N Invites sends the ticked players. A refusal of a row lands on that
 *   row, one of the whole Send above the grid, and the season is read again; a
 *   409 (the sheet changed) closes the dialog, as the sheet refetches.
 * - A read that fails with nothing to show says so with Retry; one that fails
 *   over rows already shown keeps them, says so, and turns Send off until a
 *   read succeeds.
 * - While a Send is on its way, the boxes, the fields and the season picker
 *   are locked.
 */
const ImportPreviousSeasonsModal = (props: ImportPreviousSeasonsModalProps) =>
  props.open ? <ImportPreviousSeasonsForm {...props} /> : null

const ImportPreviousSeasonsForm = ({
  open,
  teamId,
  rosterId,
  registrationId,
  seasons,
  blockedReason,
  submitting,
  onSubmit,
  onClose,
}: ImportPreviousSeasonsModalProps) => {
  'use memo'

  const selection = useImportSelection({
    teamId,
    rosterId,
    registrationId,
    initialSeasonId: seasons[0]?.id ?? null,
  })
  const { state, dispatch, rows, room, summary } = selection
  const locked = state.pending || submitting
  const canSend =
    blockedReason === null &&
    !locked &&
    !selection.readFailed &&
    summary.count > 0 &&
    !summary.overCap &&
    summary.overRoomBy === 0

  const send = () => {
    if (!canSend || state.seasonId === null) {
      return
    }
    const sent = selection.toSend()
    const names = new Map(
      rows.map((row) => [
        row.id,
        [row.name_first, row.name_last].filter(Boolean).join(' '),
      ])
    )
    dispatch({ type: 'submit', sent })
    onSubmit({ season_id: state.seasonId, players: sent })
      .then(() => {
        dispatch({ type: 'settled' })
        message.success(
          sent.length === 1
            ? balanceText('common:balanceInviteSentSuccess', {
                name: names.get(sent[0].id) ?? '',
              })
            : balanceText('common:balanceInvitesSentSuccess', {
                count: sent.length,
              })
        )
        onClose()
      })
      .catch((error) => {
        if (isConflict(error)) {
          // The sheet changed under the dialog: nothing was sent, and the sheet
          // is already refetching.
          dispatch({ type: 'settled' })
          message.error(guardMessage(error))
          onClose()
          return
        }
        dispatch({
          type: 'refused',
          invalidFields: (error as { invalid_fields?: Record<string, unknown> })
            ?.invalid_fields,
          fallbackMessage: guardMessage(error),
        })
        // What made a row unsendable (someone invited them meanwhile) shows on
        // the next read.
        selection.retry()
      })
  }

  const seasonOptions: SeasonOption[] = seasons.map((season) => ({
    value: season.id,
    label: balanceText('common:balanceImportSeasonOption', {
      season: season.schedule_name ?? '',
      team: season.team.name,
    }),
    meta: importSeasonMeta(season),
    isSelected: season.id === state.seasonId,
  }))

  const readNotice = (
    <div css={readNoticeStyle} role='alert' data-testid='import-read-error'>
      <span>{balanceText('common:balanceImportPlayersError')}</span>
      <HeaderActionButton
        variant='secondary'
        size='small'
        text={balanceLabel('common:balanceRetry')}
        onClick={selection.retry}
        data-testid='import-read-retry'
      />
    </div>
  )

  const body =
    blockedReason !== null ? (
      <InviteApprovalLock reason={blockedReason} testId='import-blocked' />
    ) : (
      <>
        <p css={introStyle}>{balanceText('common:balanceImportIntro')}</p>
        <div css={pickerStyle} data-testid='import-season-picker'>
          <SelectInput
            label={balanceLabel('common:balanceImportSeasonLabel')}
            labelSize='large'
            name='import-season'
            disabled={locked}
            allowClear={false}
            value={state.seasonId ?? ''}
            onChange={(seasonId) => dispatch({ type: 'season', seasonId })}
            options={seasonOptions}
            labelRender={renderClosedSeason}
            optionRender={renderSeasonOption}
            popupClassName={SEASON_POPUP_CLASS}
            popupMatchSelectWidth
            fieldErrors={[]}
            customError={{}}
          />
        </div>
        {state.formMessage ? (
          <p css={formErrorStyle} role='alert' data-testid='import-error'>
            {state.formMessage}
          </p>
        ) : null}
        {selection.isLoading ? (
          <ImportPreviousSeasonsSkeleton />
        ) : !selection.hasAnswer ? (
          readNotice
        ) : (
          <ImportSelectionContext.Provider
            value={{ state, dispatch, rows, summary, locked }}
          >
            <Suspense fallback={<ImportPreviousSeasonsSkeleton />}>
              <ImportPlayerGrid players={rows} />
            </Suspense>
          </ImportSelectionContext.Provider>
        )}
        {selection.hasAnswer && selection.readFailed ? readNotice : null}
        {selection.hasAnswer ? (
          <div css={hintsStyle} data-testid='import-hints'>
            {importHints(summary, room).map((hint) => (
              <p key={hint} css={hintStyle}>
                {hint}
              </p>
            ))}
          </div>
        ) : null}
      </>
    )

  return (
    <BaseModal
      isOpen={open}
      onClose={onClose}
      title={balanceLabel('common:balanceImportTitle')}
      desktopWidth='820px'
      destroyOnClose
      actions={
        <div css={footerStyle}>
          <HeaderActionButton
            variant='secondary'
            text={balanceLabel('common:cancel')}
            onClick={onClose}
            disabled={locked}
            data-testid='import-cancel'
          />
          <HeaderActionButton
            variant='primary'
            text={
              summary.count > 0 && blockedReason === null
                ? balanceLabel('common:balanceInviteSend', {
                    count: summary.count,
                  })
                : balanceLabel('common:balanceImportSendEmpty')
            }
            onClick={send}
            disabled={!canSend}
            isLoading={locked}
            data-testid='import-submit'
          />
        </div>
      }
    >
      <Global styles={seasonPopupStyle} />
      <div css={bodyStyle}>{body}</div>
    </BaseModal>
  )
}

export default ImportPreviousSeasonsModal

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`
const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const introStyle = css`
  ${paragraphMedium};
  margin: 0;
  color: ${colors.NEUTRAL_50};
  line-height: 1.5;
`
/*
 * Sized by the dialog, never by the season it shows: the closed value is one
 * line ("{season} · {team}"), which ellipsizes on a phone instead of pushing the
 * dialog past the screen's edge.
 */
const pickerStyle = css`
  contain: inline-size;
`
/** The closed picker reads at body size, like the label above it. */
const closedValueStyle = css`
  ${paragraphMedium};
  color: ${colors.NEUTRAL_0};
`
const optionStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`
const optionTextStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
`
const optionTitleStyle = css`
  ${paragraphSmall};
  ${weightMedium};
  color: ${colors.NEUTRAL_0};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const optionMetaStyle = css`
  ${paragraphXSmall};
  color: ${colors.NEUTRAL_70};
`
const optionCheckStyle = css`
  flex-shrink: 0;
  color: ${colors.NEUTRAL_50};
`
/*
 * The season list is two lines an option (it renders in a portal, so a popup
 * class reaches it, the GameDateTimeFields pattern): this dropdown, and only
 * this one, lets an option wrap.
 */
const seasonPopupStyle = css`
  .${SEASON_POPUP_CLASS} .ant-select-item-option {
    padding-top: 8px;
    padding-bottom: 8px;
  }
  .${SEASON_POPUP_CLASS} .ant-select-item-option-content {
    white-space: normal;
  }
`
const formErrorStyle = css`
  ${paragraphSmall};
  margin: 0;
  color: ${colors.ERROR_200};
  line-height: 1.5;
`
const readNoticeStyle = css`
  ${paragraphSmall};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: ${colors.NEUTRAL_50};
`
const hintsStyle = css`
  display: flex;
  flex-direction: column;
  gap: 6px;

  &:empty {
    display: none;
  }
`
const hintStyle = css`
  ${paragraphSmall};
  margin: 0;
  color: ${colors.NEUTRAL_70};
  line-height: 1.45;
`
